/**
 * The two MySQL session settings the SQL TEXT cannot disclose, read off one
 * `@@sql_mode` — which of them the masker applies is the job of
 * `sqlDelimiterPolicy.ts`, and `sqlTextMasker.ts` re-exports these so a caller
 * needs one import.
 *
 * Its own module because the SOURCE is a MySQL session variable rather than a
 * grammar: both invalidators (`ddlInvalidator.ts` for `isSchemaDdl`,
 * `navigational-features/index/indexInvalidator.ts` for `classifyDdlForIndex`)
 * feed it the same `sqlMode` field off the MySQL IntelliSense snapshot, so the
 * two maskers can never disagree about the same connection — and, because that
 * snapshot is DROPPED by the very invalidation that reads it, the last observed
 * mode is remembered here per connection
 * ({@link mysqlMaskOptionsForConnection} / {@link forgetMysqlSqlMode}).
 */

import type { MaskOptions } from './sqlDelimiterPolicy';

/** `true` when the comma-separated, case-insensitive MySQL `sql_mode` list
 *  names one of `flags`. An absent / empty value means the server default.
 *  A TOKEN match, not a substring one: `ANSI_QUOTES_OFF` is a different mode. */
function sqlModeHasAny(sqlMode: string | undefined, flags: readonly string[]): boolean {
  if (!sqlMode) return false;
  return sqlMode.split(',').some((flag) => flags.includes(flag.trim().toUpperCase()));
}

/**
 * `true` when a MySQL session's `@@sql_mode` turns `"…"` into an identifier
 * delimiter — the mode list contains `ANSI_QUOTES`, or the combination mode
 * `ANSI` (which implies it).
 */
export function mysqlAnsiQuotesFromSqlMode(sqlMode: string | undefined): boolean {
  return sqlModeHasAny(sqlMode, ['ANSI_QUOTES', 'ANSI']);
}

/** `true` when a MySQL session's `@@sql_mode` makes the backslash an ordinary
 *  character inside a string. No combination mode implies it — `ANSI` and
 *  `TRADITIONAL` both leave backslash escaping on — so only the flag itself
 *  counts. */
export function mysqlNoBackslashEscapesFromSqlMode(sqlMode: string | undefined): boolean {
  return sqlModeHasAny(sqlMode, ['NO_BACKSLASH_ESCAPES']);
}

/** Both settings at once — the shape every caller holding a MySQL snapshot
 *  needs, so neither flag can be threaded without the other. */
export function mysqlMaskOptionsFromSqlMode(sqlMode: string | undefined): MaskOptions {
  return {
    engine: 'mysql',
    mysqlAnsiQuotes: mysqlAnsiQuotesFromSqlMode(sqlMode),
    mysqlNoBackslashEscapes: mysqlNoBackslashEscapesFromSqlMode(sqlMode),
  };
}

/**
 * The `sql_mode` last OBSERVED on a MySQL connection — for the moments the
 * snapshot it is read off is not there to be read.
 *
 * `ddlInvalidator.invalidateAfterDdl` DROPS that snapshot on its way through
 * (`dropMysqlSchema`, so the next ensure is a real refresh). Capturing the
 * options before the drop settles the ONE call that did the dropping; the NEXT
 * DDL — fired before anything rebuilt the snapshot — has nothing left to read
 * and would fall back to the server default, masking the target of a
 * `DROP VIEW "v2"` that ran under `ANSI_QUOTES` as a STRING and leaving the
 * dropped view indexed.
 *
 * So the value is remembered here, the way the navigational features remember
 * their case policy (`navigational-features/casePolicyState.ts`): per
 * connection, superseded the moment a newer snapshot appears, and FORGOTTEN on
 * teardown — the app reuses a connection id across a disconnect / reconnect
 * and the next server may run a different `sql_mode`.
 * {@link forgetMysqlSqlMode} is that teardown hook.
 */
const rememberedSqlMode = new Map<string, string>();

/**
 * This connection's MySQL masking options, given the `sql_mode` its cached
 * IntelliSense snapshot carries RIGHT NOW — `undefined` when no snapshot is
 * cached (it was just dropped, or has not loaded yet).
 *
 * A live snapshot always wins and is remembered; without one the remembered
 * value answers. Only a connection nothing has ever observed a mode for falls
 * back to the MySQL server defaults.
 *
 * The read stays the CALLER's (`getCachedMysqlSchema`) so this module — and
 * through it `sqlTextMasker.ts`, which every SQL text scanner imports — keeps
 * no dependency on the connections layer.
 */
export function mysqlMaskOptionsForConnection(
  connectionId: string,
  snapshotSqlMode: string | undefined,
): MaskOptions {
  if (snapshotSqlMode !== undefined) rememberedSqlMode.set(connectionId, snapshotSqlMode);
  return mysqlMaskOptionsFromSqlMode(snapshotSqlMode ?? rememberedSqlMode.get(connectionId));
}

/** Drop a connection's remembered `sql_mode`. Called from every terminal
 *  connection path in `connections/store/connectionStore.ts` — `disconnect`,
 *  `deleteConnection` and `markConnectionDisconnected`, beside
 *  `clearSchemaRefreshesForConnection` — because a remembered mode that
 *  outlived the connection would describe the server the id used to point at.
 *  The store, not the navigational lifecycle's `forgetCasePolicy`: this value
 *  is read for every connection the DDL invalidator sees, including the ones
 *  that lifecycle never registers an index for. */
export function forgetMysqlSqlMode(connectionId: string): void {
  rememberedSqlMode.delete(connectionId);
}

/** Test-only — wipe the remembered modes between unit cases. */
export function __resetMysqlSqlModeForTest(): void {
  rememberedSqlMode.clear();
}
